import { useState, useEffect } from "react";
import { Loader2, Copy, Check, Mail, UserPlus, RotateCcw } from "lucide-react";

// INVITE A FITTER (2026-09-18).
//
// Every new fitter used to need a developer running a script, and those scripts each built the
// invite by hand -- which is how Kasey's came to have no expiry and read as "expired" the day it
// was sent. This is the button instead. The server does all of it (POST
// /api/admin/invites/fitter): one builder, the existing token scheme and 14-day window, and a
// roster row so the person lands in the right group on this tab.
//
// TWO STEPS ON PURPOSE. Minting shows the setup link to copy and sends NOTHING. Sending is a
// separate button, and it reports what the mail gate actually did: while the launch flag is off,
// an address not on the allowlist is suppressed, and saying "sent" then would be the same
// assumed-not-checked mistake the gate exists to prevent.

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function post(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = data.detail;
    // 409 carries {code, message, existing}; 422 a list of field errors; others a string.
    const msg = (d && d.message) || (Array.isArray(d) ? "Check the email address." : d)
      || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.code = (d && d.code) || "";
    err.reissuable = canReissue(d);
    throw err;
  }
  return data;
}

/** Only a lapsed or malformed invite may be re-issued; a valid one is never replaced. The server
 *  decides and says so in `existing.reissuable`; this only reads it. Exported for the test. */
export function canReissue(detail) {
  return !!(detail && ["invite_lapsed", "invite_malformed"].includes(detail.code)
            && detail.existing && detail.existing.reissuable === true);
}

const EMPTY = { first_name: "", last_name: "", email: "", company: "", group: "A" };

/** What the Send button did, in words an admin can act on. Exported for the test. */
export function verdictText(v) {
  if (!v) return "";
  if (v.verdict === "sent") return `Sent to ${v.email}.`;
  if (v.verdict === "suppressed") {
    return `Not sent — the mail gate held it back (${v.reason}). Copy the link above and send it yourself, or add the address to the mail allowlist.`;
  }
  return `Send failed: ${v.reason || "unknown error"}. The link above still works.`;
}

export default function InviteFitterCard({ onMinted }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [minted, setMinted] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [preview, setPreview] = useState(null);
  // The form as it was refused, when the refusal offers re-issue -- so the re-issue acts on
  // exactly what was refused, not on whatever the fields hold by the time the button is pressed.
  const [reissueFor, setReissueFor] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ready = form.first_name.trim() && form.last_name.trim() && form.email.trim();

  async function mint(e) {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true); setErr(""); setVerdict(null); setReissueFor(null);
    try {
      const r = await post("/api/admin/invites/fitter", form);
      setMinted(r);
      setForm(EMPTY);
      if (onMinted) onMinted();
    } catch (e2) {
      setErr(e2.message);
      if (e2.reissuable) setReissueFor({ ...form });
    }
    setBusy(false);
  }

  async function reissue() {
    if (!reissueFor || busy) return;
    setBusy(true); setErr(""); setVerdict(null);
    try {
      const r = await post("/api/admin/invites/fitter/reissue", reissueFor);
      setMinted(r);
      setForm(EMPTY);
      setReissueFor(null);
      if (onMinted) onMinted();
    } catch (e2) {
      setErr(e2.message);
      setReissueFor(null);
    }
    setBusy(false);
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(minted.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* the field is selectable; copying by hand still works */ }
  }

  // WHO IT IS GOING TO AND WHAT IT SAYS, read before the press. The press carries back the hash of what was read.
  useEffect(() => {
    setPreview(null);
    if (!minted) return;
    fetch(`${API_URL}/api/admin/invites/${minted.token}/preview`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setPreview)
      .catch(() => setPreview(null));
  }, [minted]);

  async function send() {
    if (!minted || sending || !preview) return;
    setSending(true);
    try {
      const v = await post(`/api/admin/invites/${minted.token}/send`, { message_hash: preview.message_hash });
      setVerdict(v);
      if (v.verdict === "sent" && onMinted) onMinted();
    } catch (e2) {
      setVerdict({ verdict: "failed", reason: e2.message });
    }
    setSending(false);
  }

  if (!open) {
    return (
      <button type="button" className="btn-secondary pilot-invite-open"
              onClick={() => setOpen(true)} data-testid="invite-fitter-open">
        <UserPlus size={14} /> Invite a fitter
      </button>
    );
  }

  return (
    <div className="pilot-group pilot-invite" data-testid="invite-fitter">
      <div className="pilot-group-head">
        <div>
          <div className="pilot-group-title">Invite a fitter</div>
          <div className="pilot-group-note">
            Creates their invite and puts them on the roster. Nothing is emailed until you press
            Send.
          </div>
        </div>
        <button type="button" className="pilot-refresh"
                onClick={() => { setOpen(false); setMinted(null); setErr(""); setVerdict(null); }}>
          Close
        </button>
      </div>

      <form className="pilot-invite-form" onSubmit={mint}>
        <label>First name
          <input value={form.first_name} onChange={set("first_name")} required
                 data-testid="invite-first" />
        </label>
        <label>Last name
          <input value={form.last_name} onChange={set("last_name")} required
                 data-testid="invite-last" />
        </label>
        <label>Email
          <input type="email" value={form.email} onChange={set("email")} required
                 data-testid="invite-email" />
        </label>
        <label>Company <span className="pilot-invite-opt">(optional)</span>
          <input value={form.company} onChange={set("company")} data-testid="invite-company" />
        </label>
        <fieldset className="pilot-invite-groups">
          <legend>Group</legend>
          <label>
            <input type="radio" name="group" value="A" checked={form.group === "A"}
                   onChange={set("group")} data-testid="invite-group-A" />
            Group A — certification <span className="pilot-invite-opt">(counts toward item statistics)</span>
          </label>
          <label>
            <input type="radio" name="group" value="B" checked={form.group === "B"}
                   onChange={set("group")} data-testid="invite-group-B" />
            Group B — testers <span className="pilot-invite-opt">(excluded from item statistics)</span>
          </label>
        </fieldset>
        {err && <p className="pilot-invite-err" role="alert" data-testid="invite-error">{err}</p>}
        {reissueFor && (
          <button type="button" className="btn-secondary pilot-invite-reissue" onClick={reissue}
                  disabled={busy} data-testid="invite-reissue">
            <RotateCcw size={14} /> Re-issue invite for {reissueFor.email}
          </button>
        )}
        <button type="submit" className="btn-primary" disabled={!ready || busy}
                data-testid="invite-submit">
          {busy ? <Loader2 size={14} className="pilot-spin" /> : <UserPlus size={14} />} Create invite
        </button>
      </form>

      {minted && (
        <div className="pilot-invite-result" data-testid="invite-result">
          <p>
            <b>{minted.name}</b> ({minted.email}) is on the roster in Group {minted.group}. The
            link works until {new Date(minted.expires_at).toLocaleDateString()}.
            {minted.reissued && (
              <> Re-issued: the old invite no longer works, and their existing roster row now
                 points at this link.</>
            )}
          </p>
          <div className="pilot-invite-link">
            <input readOnly value={minted.invite_url} onFocus={(e) => e.target.select()}
                   data-testid="invite-link" />
            <button type="button" className="btn-secondary" onClick={copy}
                    data-testid="invite-copy">
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          {preview && (
            <div data-testid="invite-preview">
              <p><b>To:</b> {preview.name} ({preview.to.join(", ")})</p>
              <p><b>Subject:</b> {preview.subject}</p>
              <iframe title="Invite email" sandbox="" srcDoc={preview.html}
                      style={{ width: "100%", height: 320, border: "1px solid #ddd" }} />
              {!preview.viewer_can_send && <p>Only the approver can send an invite.</p>}
            </div>
          )}
          <button type="button" className="btn-secondary" onClick={send}
                  disabled={sending || !preview || !preview.viewer_can_send || verdict?.verdict === "sent"}
                  data-testid="invite-send">
            {sending ? <Loader2 size={14} className="pilot-spin" /> : <Mail size={14} />} Send invite email
          </button>
          {verdict && (
            <p className={`pilot-invite-verdict ${verdict.verdict}`} role="status"
               data-testid="invite-verdict">
              {verdictText(verdict)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
